import { udpTrackingSaveSuccessfulAddToCartAttribution } from '../../udp-tracking/udpTrackingSaveSuccessfulAddToCartAttribution'
import {
  EXPERIMENTS_TRACK_EVENT_ADD_TO_CART,
  experimentsTrackEvent,
} from '../eventTracking'
import {
  type RecommendationAttributionAddToCartInput,
  recommendationAttributionRecordProductsAddToCart,
} from '../recommendation-attribution/recommendationAttribution'
import {
  recommendationAttributionGetProductType,
  type RecommendationProductTypeContext,
} from '../recommendation-attribution/recommendationAttributionProductType'
import { searchAttributionRecordProductsAddToCart } from '../search-attribution'

export type AddToCartSource =
  | 'cart-intermediate'
  | 'cart'
  | 'notepad'
  | 'pdp-buy-box'
  | 'pdp-buy-together'
  | 'pdp-prescription-drawer'
  | 'pdp-prescription-modal'
  | 'pdp-sticky'

type AddToCartTrackingProduct = RecommendationAttributionAddToCartInput & {
  productTypeContext: RecommendationProductTypeContext
}

type TrackProductsAddedToCartOnSuccessParams = {
  isSuccessfullyAddedToCart: boolean
  products: AddToCartTrackingProduct[]
  source: AddToCartSource
}

const UNKNOWN_PRODUCT_TYPE = 'unknown'

/**
 * Emits one canonical event per successfully added product and enriches it with
 * recommendation attribution when the product matches a previous interaction.
 */
export const addToCartExperimentsTrackProductsOnSuccess = ({
  isSuccessfullyAddedToCart,
  products,
  source,
}: TrackProductsAddedToCartOnSuccessParams): void => {
  if (!isSuccessfullyAddedToCart) {
    return
  }

  const recommendationAttributedCartItems =
    recommendationAttributionRecordProductsAddToCart(products)
  const searchAttributedCartItems =
    searchAttributionRecordProductsAddToCart(products)

  for (const [index, product] of products.entries()) {
    udpTrackingSaveSuccessfulAddToCartAttribution({
      isSuccessfullyAddedToCart,
      product,
    })

    const recommendationAttribution = recommendationAttributedCartItems[index]
    const searchAttribution = searchAttributedCartItems[index]
    const productType =
      recommendationAttribution?.productType ??
      searchAttribution?.productType ??
      recommendationAttributionGetProductType(product.productTypeContext) ??
      UNKNOWN_PRODUCT_TYPE

    experimentsTrackEvent(EXPERIMENTS_TRACK_EVENT_ADD_TO_CART, {
      $opt_event_properties: {
        ...(recommendationAttribution && {
          areaId: recommendationAttribution.areaId,
          serviceId: recommendationAttribution.serviceId,
        }),
        productType,
        recommendationAttributed: Boolean(recommendationAttribution),
        searchAttributed: Boolean(searchAttribution),
        ...(searchAttribution && {
          searchSurface: searchAttribution.surface,
        }),
        source,
      },
      value: product.quantity,
    })
  }
}

export const addToCartExperimentsTrackProductOnSuccess = ({
  isSuccessfullyAddedToCart,
  product,
  source,
}: {
  isSuccessfullyAddedToCart: boolean
  product: AddToCartTrackingProduct
  source: AddToCartSource
}): void => {
  addToCartExperimentsTrackProductsOnSuccess({
    isSuccessfullyAddedToCart,
    products: [product],
    source,
  })
}
